import React, { useMemo, useState } from 'react'
import * as XLSX from 'xlsx'

const VACWAY_DOMAINS = ['@vacway.com', '@vacway.es']

const categoryOf = (email) => {
  const e = (email || '').trim().toLowerCase()
  if (!e) return 'none'
  return VACWAY_DOMAINS.some(d => e.endsWith(d)) ? 'vacway' : 'other'
}

const CATEGORY_BADGE = {
  vacway: { label: 'Vacway', background: '#e0f2fe', color: '#075985', border: '#7dd3fc' },
  other: { label: 'Externo', background: '#fef3c7', color: '#92400e', border: '#fcd34d' },
  none: { label: 'Sin email', background: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
}

const CustomerEmailsModal = ({ customers, onClose }) => {
  const [filter, setFilter] = useState('all')

  const rows = useMemo(() => (
    (customers || [])
      .map(c => ({
        fullname: c.Fullname || '-',
        email: (c.Email || '').trim(),
        type: c.Type || '-',
        category: categoryOf(c.Email),
      }))
      .sort((a, b) => a.fullname.localeCompare(b.fullname, 'es', { sensitivity: 'base' }))
  ), [customers])

  const counts = useMemo(() => ({
    all: rows.length,
    vacway: rows.filter(r => r.category === 'vacway').length,
    other: rows.filter(r => r.category === 'other').length,
    none: rows.filter(r => r.category === 'none').length,
  }), [rows])

  const filteredRows = filter === 'all' ? rows : rows.filter(r => r.category === filter)

  const handleDownloadExcel = () => {
    const data = filteredRows.map(r => ({
      Fullname: r.fullname,
      Email: r.email || '-',
    }))

    const worksheet = XLSX.utils.json_to_sheet(data)
    worksheet['!cols'] = [{ wch: 35 }, { wch: 35 }]
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Emails')

    const suffix = filter === 'all' ? '' : (filter === 'vacway' ? '-vacway' : (filter === 'other' ? '-externos' : '-sin-email'))
    XLSX.writeFile(workbook, `emails-clientes${suffix}.xlsx`)
  }

  const chipStyle = (key, activeColor) => ({
    padding: '6px 14px',
    borderRadius: 20,
    border: `1px solid ${filter === key ? activeColor : '#d1d5db'}`,
    background: filter === key ? activeColor : '#fff',
    color: filter === key ? '#fff' : '#374151',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  })

  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 8, padding: 24, width: '95%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: '#004f75' }}>
            <i className="fas fa-envelope mr-2" />
            Emails de Clientes
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666' }}>✕</button>
        </div>

        {/* Domain analysis */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <button onClick={() => setFilter('all')} style={chipStyle('all', '#004f75')}>
            Todos ({counts.all})
          </button>
          <button onClick={() => setFilter('vacway')} style={chipStyle('vacway', '#0e7490')}>
            @vacway.com / .es ({counts.vacway})
          </button>
          <button onClick={() => setFilter('other')} style={chipStyle('other', '#d97706')}>
            Otros dominios ({counts.other})
          </button>
          <button onClick={() => setFilter('none')} style={chipStyle('none', '#6b7280')}>
            Sin email ({counts.none})
          </button>
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', flex: 1, border: '1px solid #eee', borderRadius: 6 }}>
          {filteredRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#999', fontSize: '0.875rem' }}>
              No hay clientes en esta categoría.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f0f4fa', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#004f75' }}>Nombre</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#004f75' }}>Email</th>
                  <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#004f75' }}>Dominio</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, idx) => {
                  const badge = CATEGORY_BADGE[r.category]
                  return (
                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fb', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px 12px', color: '#333' }}>{r.fullname}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#333' }}>{r.email || '-'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: badge.background, color: badge.color, border: `1px solid ${badge.border}` }}>
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 18px', background: '#e5e7eb', color: '#333', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}
          >
            Cerrar
          </button>
          <button
            onClick={handleDownloadExcel}
            disabled={filteredRows.length === 0}
            style={{ padding: '8px 18px', background: filteredRows.length === 0 ? '#9ca3af' : '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: filteredRows.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.875rem' }}
          >
            <i className="fas fa-file-excel mr-1" />
            Descargar Excel ({filteredRows.length})
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomerEmailsModal
